import { PasswordInput as DojoPasswordInput } from '@casumo/dojo-react';
import classNames from 'classnames';
import {
  ChangeEvent,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ValidationRuleItem } from './validationRuleItem.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TEsPasswordInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import validateEsPassword, {
  EsPasswordState,
  getPasswordState,
  isEsPasswordValid,
} from '../../utils/esPasswordValidation.ts';
import { GTM_ERRORS } from '../../utils/gtm/constants.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';

const getGtmAlertForState = (state: EsPasswordState): string => {
  switch (state) {
  case 'simple_pattern':
    return GTM_ERRORS.INVALID_PASSWORD_SIMPLE_PATTERN;
  case 'personal_details':
    return GTM_ERRORS.INVALID_PASSWORD_PERSONAL_DETAILS;
  case 'pattern_and_personal':
    return `${GTM_ERRORS.INVALID_PASSWORD_SIMPLE_PATTERN}|${GTM_ERRORS.INVALID_PASSWORD_PERSONAL_DETAILS}`;
  case 'unticked_requirements':
    return GTM_ERRORS.INVALID_PASSWORD_UNTICKED_REQUIREMENTS;
  case 'all_valid':
    return GTM_ERRORS.VALID_PASSWORD;
  default:
    return GTM_ERRORS.INVALID_PASSWORD;
  }
};

const EsPasswordInput = ({
  name,
  value,
  label,
  ruleMinLength,
  ruleHasLetter,
  ruleHasNumber,
  ruleHasSpecialChar,
  validationPanelTitle,
  warningNote,
  simplePatternError,
  personalDetailsError,
  patternAndPersonalError,
  untickedRequirementsError,
  successMessage,
  onBlur,
  onKeyDown,
}: TEsPasswordInputField & {
  onBlur?: (field: Partial<TEsPasswordInputField>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
}) => {
  const {
    errors,
    setErrors,
    formData,
    setFormData,
    customValidations,
    setCustomValidations,
    metadata,
  } = useRegistrationStore();

  const [isFocused, setIsFocused] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastAlertRef = useRef<string | null>(null);

  // DojoPasswordInput overrides onFocus for its show/hide mask, so we track focus
  // via a wrapper using bubbling focusin/focusout.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const handleFocusIn = () => {
      setIsFocused(true);
      lastAlertRef.current = null;
    };
    const handleFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
      }
    };
    el.addEventListener('focusin', handleFocusIn);
    el.addEventListener('focusout', handleFocusOut);

    return () => {
      el.removeEventListener('focusin', handleFocusIn);
      el.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const password = useMemo(
    () => value || formData?.[name] || '',
    [value, formData, name],
  );

  const ruleLabels = useMemo(
    () => ({
      minLength:      ruleMinLength || '',
      hasLetter:      ruleHasLetter || '',
      hasNumber:      ruleHasNumber || '',
      hasSpecialChar: ruleHasSpecialChar || '',
    }),
    [ruleMinLength, ruleHasLetter, ruleHasNumber, ruleHasSpecialChar],
  );

  const errorMessages = useMemo(
    () => ({
      simplePattern:        simplePatternError,
      personalDetails:      personalDetailsError,
      patternAndPersonal:   patternAndPersonalError,
      untickedRequirements: untickedRequirementsError,
      success:              successMessage,
    }),
    [simplePatternError, personalDetailsError, patternAndPersonalError, untickedRequirementsError, successMessage],
  );

  const validationResult = useMemo(
    () => validateEsPassword(password, formData, ruleLabels),
    [password, formData, ruleLabels],
  );

  const passwordState = useMemo(
    () => getPasswordState(validationResult, password),
    [validationResult, password],
  );

  const getErrorMessage = useCallback((state: EsPasswordState): string => {
    switch (state) {
    case 'simple_pattern':
      return errorMessages.simplePattern || '';
    case 'personal_details':
      return errorMessages.personalDetails || '';
    case 'pattern_and_personal':
      return errorMessages.patternAndPersonal || '';
    case 'unticked_requirements':
      return errorMessages.untickedRequirements || '';
    default:
      return '';
    }
  }, [errorMessages]);

  const getBlurErrorMessage = useCallback((state: EsPasswordState): string => {
    const baseError = getErrorMessage(state);
    
    if (
      (state === 'simple_pattern' || state === 'personal_details' || state === 'pattern_and_personal') &&
      !validationResult.isAllRulesValid
    ) {
      return `${baseError} ${errorMessages.untickedRequirements}`;
    }
    
    return baseError;
  }, [getErrorMessage, validationResult.isAllRulesValid, errorMessages.untickedRequirements]);

  const fireGtmAlert = useCallback((state: EsPasswordState) => {
    const alertType = getGtmAlertForState(state);
    
    if (lastAlertRef.current === alertType) return;
    lastAlertRef.current = alertType;

    GtmEvents.logAlert({
      alert: alertType,
      step:  metadata?.currentStep,
    });
  }, [metadata?.currentStep]);

  const handleBlur = useCallback((e: { relatedTarget: EventTarget | null }) => {
    // If focus is moving to another element within the wrapper (e.g. show/hide button), skip blur logic
    if (wrapperRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }

    setHasBlurred(true);

    if (!password) {
      setCustomValidations({
        ...customValidations,
        [name]: errorMessages.untickedRequirements || '',
      });

      fireGtmAlert('unticked_requirements');

      return;
    }

    const isValid = isEsPasswordValid(password, formData);

    if (!isValid) {
      const errorMsg = getBlurErrorMessage(passwordState);
      setCustomValidations({
        ...customValidations,
        [name]: errorMsg,
      });

      fireGtmAlert(passwordState);

      return;
    }

    setCustomValidations({
      ...customValidations,
      [name]: '',
    });

    fireGtmAlert('all_valid');

    if (onBlur) {
      onBlur({ name });
    }
  }, [
    password, name, formData, passwordState, customValidations, errorMessages.untickedRequirements,
    setCustomValidations, getBlurErrorMessage, fireGtmAlert, onBlur,
  ]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.currentTarget.value;

      setErrors({ ...errors, [name]: '' });
      setCustomValidations({ ...customValidations, [name]: '' });
      setFormData({ [name]: newValue });

      if (newValue) {
        const result = validateEsPassword(newValue, formData, ruleLabels);
        const state = getPasswordState(result, newValue);
        
        if (state !== 'all_valid' && state !== 'unticked_requirements') {
          fireGtmAlert(state);
        }
      }
    },
    [errors, customValidations, name, setErrors, setCustomValidations, setFormData, 
      formData, ruleLabels, fireGtmAlert],
  );

  const showValidationPanel = isFocused;
  const hasError = !!(errors[name] || customValidations[name]);
  const showPostBlurMessage = !isFocused && (hasBlurred || hasError);

  const showFocusedError = isFocused && password && 
    (passwordState === 'simple_pattern' || 
     passwordState === 'personal_details' || 
     passwordState === 'pattern_and_personal');

  return (
    <div ref={wrapperRef} className="w-full flex flex-col gap-2xs font-circular-pro">
      <div
        onClick={
          !metadata?.isEmailValid
            ? () => {
                GtmEvents.logAlert({
                  alert: GTM_ERRORS.DISABLED_PASSWORD_CLICK,
                  step:  metadata?.currentStep,
                });
              }
            : undefined
        }
        className={classNames({ 'pointer-events-none': !metadata?.isEmailValid })}
      >
        <DojoPasswordInput
          id="esPasswordInput"
          autoComplete="new-password"
          name={name}
          label={label}
          value={password}
          error={hasError}
          disabled={!metadata?.isEmailValid}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          data-testid={TEST_ID.INPUT + 'plainTextPassword'}
        />
      </div>

      {showFocusedError && !showPostBlurMessage && getErrorMessage(passwordState) && (
        <p className="text-red-30 text-body-xs animate-fadeIn">
          {getErrorMessage(passwordState)}
        </p>
      )}

      {showValidationPanel && (
        <div
          className="p-xs rounded-lg text-sm bg-grey-0"
          data-testid={TEST_ID.PREFIX + 'es-password-validation-panel'}
        >
          <div className="flex flex-col gap-xs">
            {validationPanelTitle && (
              <p className="text-grey-70 text-body-2xs">
                {validationPanelTitle}
              </p>
            )}
            <ul className="space-y-1.5">
              {validationResult.rules.map((rule) => (
                <ValidationRuleItem key={rule.id} rule={rule} animate />
              ))}
            </ul>
            {warningNote && (
              <p className="text-grey-70 text-body-2xs leading-normal" dangerouslySetInnerHTML={{ 
                __html: warningNote }}>
              </p>
            )}
          </div>
        </div>
      )}

      {showPostBlurMessage && (
        <>
          {passwordState === 'all_valid' ? (
            errorMessages.success && (
              <div className="text-body-xs text-green-30 animate-fadeIn">
                {errorMessages.success}
              </div>
            )
          ) : (
            getBlurErrorMessage(passwordState) && (
              <div className="text-red-30 text-body-xs animate-fadeIn">
                {getBlurErrorMessage(passwordState)}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

const Memoized = memo(EsPasswordInput);
Memoized.displayName = 'EsPasswordInput';

export default Memoized;
